import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState, useMemo } from 'react'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useTenant } from '@emerald-works-nova/auth'
import { useAlert, useGroup, useExternalApp, useFormFactor, usePaidConsumer, useIsOnTrial } from '../../hooks'
import { ALL_ADMIN, ALL_ENTERPRISE, CONSUMER } from '../../services/cognito/userGroups'
import { useStyles } from './style'
import {
  Drawer,
  Typography,
  Box,
  IconButton
} from '@mui/material'
import { ActionButton } from '../action-buttons'
import { Link } from 'react-router-dom'
import CloseIcon from '@mui/icons-material/Close'
import { useTheme } from '@emotion/react'
import { FreeAccountCtaLock } from '../icons'

const MindsetAgent = ({ open, setOpen }, ref) => {
  const [from, setFrom] = useState()
  const { isInGroup } = useGroup()
  const { isPaidConsumer } = usePaidConsumer()
  const { isTrialBannerVisible } = useIsOnTrial()
  const { isSmallTablet, isLargeTablet, isMobile: isSmartphone } = useFormFactor()
  const isMobile = isSmallTablet || isLargeTablet || isSmartphone
  const classes = useStyles({ isPaidConsumer, isMobile, isTrialBannerVisible })
  const { enqueueMsg } = useAlert()
  const tenant = useTenant()
  const { getMtUrl } = useExternalApp()
  const theme = useTheme()
  const [authTokenResponse, setAuthTokenResponse] = useState(null)
  const [initialQuestion, setInitialQuestion] = useState(null)
  const [pendingOpen, setPendingOpen] = useState(null)
  const [sessionKey, setSessionKey] = useState(0)
  const hasMindsetAiAccess = isInGroup([...ALL_ENTERPRISE, ...ALL_ADMIN, CONSUMER]) && tenant.features?.mindsetAi

  const tenantId = tenant?.tenantKey ? `${tenant?.tenantKey}.` : ''
  const agentUid = authTokenResponse?.agentSession?.agentSessionUid || process.env.REACT_APP_MINDSET_AGENT_UID

  const [askMCoachSignUpClicks] = useEvent([{ eventName: 'askMCoachSignUpClicks' }])

  const mindsetTheme = useMemo(() => {
    return {
      brightness: 'light',
      seedColor: theme.palette.primary.main,
      surface: '#FFFFFF',
      dynamicSchemeVariant: 'fidelity'
    }
  }, [theme])

  const [getAuthToken] = useEvent([{
    eventName: 'getAuthToken',
    onSuccess: (payload) => {
      setAuthTokenResponse(payload)
    },
    onError: (error) => {
      enqueueMsg('Unable to retrieve auth token', 'error')
      console.error('Unable to retrieve auth token', error)
    }
  }])

  const onClose = useCallback(() => {
    setOpen(false)
    setInitialQuestion(null)
  }, [setOpen])

  useImperativeHandle(ref, () => ({
    close: onClose,
    open: ({ initialQuestion, from }) => {
      setOpen(true)
      setFrom(from)
      setInitialQuestion(initialQuestion)
    },
    openNewSession: ({ initialQuestion, from }) => {
      onClose()
      // Fully reset the mindset SDK so init() creates a fresh Flutter app.
      // - _initialized: false → allows init() to re-run
      // - flutterApp: null → forces new Flutter instance creation
      // - handleReceivedMessage: null → prevents auth being sent to old
      //   Flutter app; init() will store it as pendingMessage instead,
      //   which the new Flutter app picks up via checkForPendingMessage()
      // - pendingMessage: null → clears stale queued messages
      try {
        if (window._mindset_) {
          window._mindset_._initialized = false
          window._mindset_.flutterApp = null
          window._mindset_.handleReceivedMessage = null
          window._mindset_.pendingMessage = null
        }
      } catch (e) {
        console.error('Error cleaning up mindset state:', e)
      }
      setSessionKey(prev => prev + 1)
      setAuthTokenResponse(null)
      setPendingOpen({ initialQuestion, from })
    },
    isOpen: () => open
  }), [onClose, open, setOpen])

  useEffect(() => {
    if (!authTokenResponse && hasMindsetAiAccess) {
      getAuthToken.trigger()
    }
  }, [authTokenResponse, getAuthToken, hasMindsetAiAccess])

  useEffect(() => {
    if (authTokenResponse && pendingOpen) {
      setOpen(true)
      setFrom(pendingOpen.from)
      setInitialQuestion(pendingOpen.initialQuestion)
      setPendingOpen(null)
    }
  }, [authTokenResponse, pendingOpen, setOpen])

  useEffect(() => {
    if (authTokenResponse) {
      try {
        window.mindset.init({
          appUid: 'mindtools_coach',
          theme: mindsetTheme,
          fetchAuthentication: async () => {
            return authTokenResponse.authToken
          }
        })
      } catch (error) {
        console.error('Error loading Mindset Agent:', error)
      }
    } else {
      console.log('Mindset Agent is already connected or auth response is not available yet')
    }
  }, [authTokenResponse, mindsetTheme, tenantId])

  // Unlock body scroll when drawer closes (safety net for mouseenter/mouseleave)
  useEffect(() => {
    if (!open) document.body.style.overflow = ''
  }, [open])

  if (!hasMindsetAiAccess) {
    return (
      <Drawer
        open={open}
        className={classes.root}
        variant='persistent'
        anchor='right'
        data-test='mindset-agent-drawer'
      >
        <Box className={classes.container} data-test='mindset-agent-container'>
          <Box className={classes.close} display='flex' justifyContent='flex-end' data-test='mindset-agent-close-button-container'>
            <IconButton
              aria-label='close'
              onClick={onClose}
              data-test='mindset-agent-close-button'
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box className={classes.background} />
          <Box className={classes.ctaContainer}>
            <Box className={classes.ctaContent}>
              <FreeAccountCtaLock />
              <Typography className={classes.message} data-test='mindset-ai-cta-message'>
                You&#39;ll need to be a Mindtools Member to access this feature
              </Typography>
              <Box width='100%'>
                <ActionButton
                  variant='primary'
                  label='Join Today'
                  data-test='mindset-ai-cta-create-account'
                  data-gtm='mindset-ai-cta-create-account'
                  href={getMtUrl('join')}
                  className={classes.joinMessage}
                  onClick={() => askMCoachSignUpClicks.trigger({ from })}
                />
              </Box>
              <Typography className={classes.goToSignMessage}>Already have an account? <Link to='/login' data-test='mindset-ai-cta-sign-in'>Sign in</Link></Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    )
  }

  return (
    <Drawer
      open={open}
      className={classes.root}
      variant='persistent'
      anchor='right'
      data-test='mindset-agent-drawer'
    >
      <Box
        className={classes.container}
        data-test='mindset-agent-container'
        onMouseEnter={() => { document.body.style.overflow = 'hidden' }}
        onMouseLeave={() => { document.body.style.overflow = '' }}
      >
        <Box className={classes.close} display='flex' justifyContent='flex-end' data-test='mindset-agent-close-button-container'>
          <IconButton
            aria-label='close'
            onClick={onClose}
            data-test='mindset-agent-close-button'
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant='h5' align='center' gutterBottom data-test='mindset-agent-title'>
          Ask M: Coach
        </Typography>
        {authTokenResponse && open && (
          <mindset-agent
            key={sessionKey}
            id='agent-div'
            data-test='mindset-agent-div'
            agentUid={agentUid}
            variables={JSON.stringify({ tenantId })}
            initialQuestion={initialQuestion}
            style={{
              flexGrow: 1,
              width: '100%',
              marginLeft: 'auto',
              marginRight: 'auto',
              overflow: 'hidden',
              overscrollBehavior: 'contain',
              boxSizing: 'border-box'
            }}
          />
        )}
      </Box>
    </Drawer>
  )
}

export default forwardRef(MindsetAgent)
